// components/CustomerSelectPanel.tsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout, Typography, Button, List, Tag, Space, Select, Input, Form, Row, Col, message, Spin } from 'antd';
import {
  ArrowLeftOutlined, UserOutlined, BankOutlined, DownOutlined, UpOutlined,
  MailOutlined, HomeOutlined, FileTextOutlined, SearchOutlined, FilterOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { businessCardAPI } from '../services/api';
import { Customer, CustomerSearchRequest } from '../types/businessCard';
import { generateRankColors, getRankColorByMasterOrder } from '../services/rank-utils';
import { useRankColors } from '../hooks/useRankColors';
import { useRankOptions } from '../hooks/useRankOptions';
import { indexedDBStorage } from '../services/storage/indexedDB';
import { useContactUsers } from '../hooks/useContactUsers';

const { Header, Content } = Layout;
const { Text } = Typography;


interface CustomerSelectPanelProps {
  visible: boolean;
  onClose: () => void;
  defaultCompany?: string;
  onSelectionChange?: (customers: Customer[]) => void;
  selectedCustomersFromParent?: Customer[];
  zIndex?: number;
  excludeCustomerNo?: number;
  flutterFooterHeight?: number;
}

const CustomerSelectPanel: React.FC<CustomerSelectPanelProps> = ({ visible, onClose, defaultCompany, onSelectionChange, selectedCustomersFromParent, zIndex = 1000, excludeCustomerNo: excludeCustomerNoProp, flutterFooterHeight = 0 }) => {
  const pageSize = 20;
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [searchExpanded, setSearchExpanded] = useState<boolean>(false);
  const [advancedSearchExpanded, setAdvancedSearchExpanded] = useState<boolean>(false);
  const [form] = Form.useForm();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [rankColors, setRankColors] = useRankColors();
  const { rankOptions, isLoading: isLoadingRanks, refetch: refetchRanks } = useRankOptions();

  const { contactUsers, isLoading: isLoadingContactUsers, refetch: refetchContactUsers } = useContactUsers();
  const [selectedContactUsers, setSelectedContactUsers] = useState<number[]>([]);

  const [searchParams, setSearchParams] = useState<CustomerSearchRequest>({
    limit: pageSize,
    offset: 0,
    usecustomernamefurigana: true,
    usecompanynamefurigana: true,
  });

  const [hasMore, setHasMore] = useState<boolean>(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [excludeCustomerNo, setExcludeCustomerNo] = useState<number | null>(null);
  const [selectedCustomerNos, setSelectedCustomerNos] = useState<Set<number>>(new Set());
  // 選択済み顧客の全情報を保持（パネルを閉じても維持）
  const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([]);



  const assignRankColors = (list: Customer[]) => {
    setRankColors(generateRankColors(list));
  };

  // 顧客一覧取得
  const fetchCustomers = useCallback(async (params: CustomerSearchRequest, append: boolean = false) => {
    setIsLoading(true);
    try {
      const response = await businessCardAPI.searchCustomers(params);
      if (response.statuscode === 200) {
        const newList: Customer[] = (response.customerlist || []).map((c: Customer) => ({
          ...c,
          position: '',
        }));
        setCustomers(prev => append ? [...prev, ...newList] : newList);
        setTotalCount(response.customerlistsize);
        setHasMore(params.offset + newList.length < response.customerlistsize);
        assignRankColors(append ? [...customers, ...newList] : newList);
      } else {
        message.error(response.message || 'データの取得に失敗しました');
        if (!append) { setCustomers([]); setTotalCount(0); }
        setHasMore(false);
      }
    } catch {
      message.error('データの取得中にエラーが発生しました');
      if (!append) { setCustomers([]); setTotalCount(0); }
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [customers]); // eslint-disable-line

  // 追加読み込み
  const loadMoreData = useCallback(() => {
    if (isLoading || !hasMore) return;
    const nextParams = { ...searchParams, offset: customers.length };
    setSearchParams(nextParams);
    fetchCustomers(nextParams, true);
  }, [isLoading, hasMore, searchParams, customers.length, fetchCustomers]);



  // パネル表示中はbodyのスクロールを無効化
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [visible]);

  // パネルが開いたときに初期化
  useEffect(() => {
    if (!visible) return;
    form.resetFields();
    setSelectedContactUsers([]);
    setExpandedItems(new Set());
    setSearchExpanded(false);
    setAdvancedSearchExpanded(false);
    setCustomers([]);
    setHasMore(true);

    // 親から渡された選択状態を同期
    if (selectedCustomersFromParent) {
      setSelectedCustomers(selectedCustomersFromParent);
      setSelectedCustomerNos(new Set(selectedCustomersFromParent.map(c => c.customerno || c.no)));
    }

    // 編集中の顧客番号を取得してフィルタ用に保持（propが渡された場合は優先）
    if (excludeCustomerNoProp !== undefined) {
      setExcludeCustomerNo(excludeCustomerNoProp);
    } else {
      indexedDBStorage.getItem('editingCustomerNo').then(val => {
        const no = val ? parseInt(val, 10) : null;
        setExcludeCustomerNo(!isNaN(no as number) ? no : null);
      });
    }

    const initialParams: CustomerSearchRequest = {
      limit: pageSize,
      offset: 0,
      usecustomernamefurigana: true,
      usecompanynamefurigana: true,
      ...(defaultCompany ? { companyname: defaultCompany } : {}),
    };

    if (defaultCompany) {
      form.setFieldsValue({ company: defaultCompany });
    }

    setSearchParams(initialParams);
    refetchContactUsers();
    refetchRanks();
    fetchCustomers(initialParams);
  }, [visible]); // eslint-disable-line

  // パネル内スクロールで無限スクロール
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight - 300 && !isLoading && hasMore) {
        loadMoreData();
      }
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [loadMoreData, isLoading, hasMore]);

  // 検索実行
  const handleSearch = async () => {
    const values = await form.validateFields();
    const newParams: CustomerSearchRequest = {
      limit: pageSize,
      offset: 0,
      usecustomernamefurigana: true,
      usecompanynamefurigana: true,
    };
    if (values.customerName) newParams.customername = values.customerName.trim();
    if (values.mailAddress) newParams.mailaddress = values.mailAddress.trim();
    if (values.company) newParams.companyname = values.company.trim();
    if (values.address) newParams.address = values.address.trim();
    if (values.remarks) newParams.remarks = values.remarks.trim();
    if (values.phone) newParams.telMobiletel = values.phone.trim();
    if (values.rank) newParams.rankno = parseInt(values.rank);
    if (selectedContactUsers.length > 0) newParams.contactusernoList = selectedContactUsers;
    setHasMore(true);
    setCustomers([]);
    setSearchParams(newParams);
    fetchCustomers(newParams);
  };

  // 担当者セレクター
  const renderContactUsersSelector = () => (
    <div>
      <div style={{ marginBottom: selectedContactUsers.length > 0 ? '8px' : 0 }}>
        {selectedContactUsers.map(userno => {
          const user = contactUsers.find(u => u.userno === userno);
          return (
            <Tag
              key={userno}
              style={{ padding: '4px 8px', marginBottom: '6px', fontSize: '14px', display: 'inline-flex', alignItems: 'center' }}
              closable
              onClose={e => { e.preventDefault(); setSelectedContactUsers(prev => prev.filter(id => id !== userno)); }}
            >
              {user ? user.username : (userno < 0 ? 'システム管理者' : `担当者(ID:${userno})`)}
            </Tag>
          );
        })}
      </div>
      <Select
        style={{ width: '100%', marginTop: '8px' }}
        placeholder="+ 担当者を追加"
        onChange={value => {
          if (value && !selectedContactUsers.includes(Number(value))) {
            setSelectedContactUsers(prev => [...prev, Number(value)]);
          }
        }}
        value={null}
        loading={isLoadingContactUsers}
        dropdownStyle={{ zIndex: zIndex + 100 } as React.CSSProperties}
      >
        {contactUsers
          .filter(u => !selectedContactUsers.includes(u.userno))
          .map(u => (
            <Select.Option key={u.userno} value={u.userno}>
              {u.username} ({u.deptname || '-'})
            </Select.Option>
          ))}
      </Select>
    </div>
  );

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: zIndex,
        transform: visible ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        background: '#f0f2f5',
        paddingBottom: flutterFooterHeight > 0 ? `${flutterFooterHeight}px` : 0,
        boxSizing: 'border-box',
      }}
    >
      <Layout style={{ height: '100%', overflow: 'hidden' }}>
        {/* ヘッダー */}
        <Header style={{
          background: '#fff',
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          height: 'auto',
          minHeight: '64px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          flexShrink: 0,
        }}>
          {/* 戻るボタン：選択状態を反映せずに閉じるだけ */}
          <Button
            shape="circle"
            size="small"
            icon={<ArrowLeftOutlined />}
            onClick={onClose}
            style={{
              backgroundColor: 'white',
              borderColor: '#d9d9d9',
              borderWidth: '2px',
              color: '#595959',
              marginRight: '8px',
              width: '32px',
              height: '32px',
              minWidth: '32px',
              minHeight: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1, textAlign: 'center' }}>
            顧客選択
          </div>
          {/* 追加確定ボタン：右端に配置 */}
          <Button
            type="primary"
            disabled={selectedCustomerNos.size === 0}
            onClick={() => {
              onSelectionChange?.(selectedCustomers);
              onClose();
            }}
            style={{
              minWidth: '72px',
              fontWeight: 'bold',
            }}
          >
            {selectedCustomerNos.size > 0
              ? `追加 (${selectedCustomerNos.size})`
              : '追加'}
          </Button>
        </Header>

        {/* スクロール可能なコンテンツ */}
        <div ref={scrollContainerRef} style={{ flex: 1, overflowY: 'auto' }}>
          <Content style={{ padding: '16px', background: '#f0f2f5' }}>
            {/* 検索エリア */}
            <div style={{
              background: '#fff',
              borderRadius: '4px',
              marginBottom: '16px',
              overflow: 'hidden',
              transition: 'all 0.3s',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}>
              <div
                style={{
                  padding: '10px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: searchExpanded ? '1px solid #f0f0f0' : 'none',
                  cursor: 'pointer',
                  background: '#f5faff',
                }}
                onClick={() => setSearchExpanded(!searchExpanded)}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <SearchOutlined style={{ marginRight: '8px', color: '#0693e3' }} />
                  <Text strong>検索</Text>
                </div>
                <Button
                  type="text"
                  icon={searchExpanded ? <UpOutlined /> : <DownOutlined />}
                  size="small"
                  onClick={e => { e.stopPropagation(); setSearchExpanded(!searchExpanded); }}
                />
              </div>

              {searchExpanded && (
                <div style={{ padding: '16px' }}>
                  <Form form={form} layout="vertical">
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item label="会社名" name="company">
                          <Input placeholder="会社名で検索" prefix={<BankOutlined style={{ color: '#bfbfbf' }} />} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item label="顧客名" name="customerName">
                          <Input placeholder="顧客名で検索" prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} />
                        </Form.Item>
                      </Col>
                    </Row>

                    <div
                      style={{ marginTop: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                      onClick={() => setAdvancedSearchExpanded(!advancedSearchExpanded)}
                    >
                      <FilterOutlined style={{ marginRight: '8px', color: '#0693e3' }} />
                      <Text type="secondary">詳細検索</Text>
                      {advancedSearchExpanded
                        ? <UpOutlined style={{ marginLeft: '8px', fontSize: '12px' }} />
                        : <DownOutlined style={{ marginLeft: '8px', fontSize: '12px' }} />}
                    </div>

                    {advancedSearchExpanded && (
                      <>
                        <Row gutter={16}>
                          <Col xs={24} sm={12} md={8}>
                            <Form.Item label="ランク" name="rank">
                              <Select placeholder="ランクを選択" allowClear loading={isLoadingRanks} options={rankOptions} dropdownStyle={{ zIndex: zIndex + 100 } as React.CSSProperties} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={16}>
                            <Form.Item label="担当者" name="contactUsers">
                              {renderContactUsersSelector()}
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={16}>
                          <Col xs={24} sm={12} md={8}>
                            <Form.Item label="TEL/携帯" name="phone">
                              <Input placeholder="電話番号" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={8}>
                            <Form.Item label="住所" name="address">
                              <Input placeholder="住所" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={8}>
                            <Form.Item label="E-mail" name="mailAddress">
                              <Input placeholder="メールアドレスで検索" prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={16}>
                          <Col xs={24}>
                            <Form.Item label="備考" name="remarks">
                              <Input.TextArea placeholder="備考に含まれる文字列" rows={2} />
                            </Form.Item>
                          </Col>
                        </Row>
                      </>
                    )}

                    <Row justify="end" gutter={16} style={{ marginTop: '16px' }}>
                      <Col>
                        <Button onClick={() => {
                          // unmount 中に setFieldsValue で書き込まれた値が resetFields で消えないケースに備えて
                          // 各フィールドを明示的に undefined にしてから resetFields する
                          form.setFieldsValue({
                            company: undefined,
                            customerName: undefined,
                            mailAddress: undefined,
                            address: undefined,
                            remarks: undefined,
                            phone: undefined,
                            rank: undefined,
                          });
                          form.resetFields();
                          setSelectedContactUsers([]);
                        }}>
                          クリア
                        </Button>
                      </Col>
                      <Col>
                        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={isLoading}>
                          検索
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </div>
              )}
            </div>

            {/* 件数とオプション表示エリア */}
            <div style={{ marginBottom: '16px' }}>
              {/* 件数表示 */}
              <div style={{
                padding: '10px 16px',
                background: '#f0f7ff',
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderLeft: '3px solid #0693e3',
                borderRight: '1px solid #e8e8e8',
                borderBottom: '1px solid #e8e8e8',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              }}>
                <Text>
                  全 <strong>{excludeCustomerNo && customers.some(c => (c.customerno || c.no) === excludeCustomerNo) ? totalCount - 1 : totalCount}</strong> 件
                  {isLoading && <LoadingOutlined style={{ marginLeft: 8 }} />}
                </Text>
              </div>
            </div>

            {/* リスト */}
            {customers.length === 0 && isLoading ? (
              <div style={{ textAlign: 'center', padding: '32px', background: '#fff', borderRadius: '4px', marginTop: '16px' }}>
                <LoadingOutlined style={{ fontSize: 24 }} />
                <div style={{ marginTop: 16 }}>データを読み込み中...</div>
              </div>
            ) : customers.length > 0 ? (
              <div>
                <List
                  itemLayout="horizontal"
                  dataSource={customers.filter(c => (c.customerno || c.no) !== excludeCustomerNo)}
                  renderItem={item => {
                    const itemId = item.customerno || item.no;
                    const isSelected = selectedCustomerNos.has(itemId);
                    return (
                    <List.Item
                      onClick={() => {
                        setSelectedCustomerNos(prev => {
                          const next = new Set(prev);
                          next.has(itemId) ? next.delete(itemId) : next.add(itemId);
                          const nextCustomers = next.has(itemId)
                            ? [...selectedCustomers.filter(c => (c.customerno || c.no) !== itemId), item]
                            : selectedCustomers.filter(c => (c.customerno || c.no) !== itemId);
                          setSelectedCustomers(nextCustomers);
                          return next;
                        });
                      }}
                      style={{
                        padding: '12px 16px',
                        marginBottom: '16px',
                        background: isSelected ? '#f6ffed' : '#fff',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                        border: isSelected ? '1px solid #b7eb8f' : '1px solid transparent',
                        transition: 'background 0.2s, border-color 0.2s',
                      }}
                    >
                      <div style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                            {/* 名刺情報 */}
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                <Tag
                                  color={(() => {
                                    // 地図・詳細・一覧と同じ「ランクマスタ順位」ベースで色を決める
                                    const color = getRankColorByMasterOrder(
                                      item.rankno,
                                      rankOptions.map((o) => ({ rankno: Number(o.value) })),
                                    );
                                    if (color !== '#bfbfbf') return color;
                                    if (item.rankname) return rankColors[item.rankname] || '#bfbfbf';
                                    return '#bfbfbf';
                                  })()}
                                  style={{
                                    minWidth: '20px', maxWidth: '70px', height: 'auto', lineHeight: '18px',
                                    textAlign: 'center', padding: '0 8px', fontSize: '12px', fontWeight: 'bold',
                                    marginRight: '8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                  }}
                                >
                                  {item.rankname || ''}
                                </Tag>
                                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{item.customername}</div>
                              </div>
                              <div>
                                <Text style={{ fontSize: '14px' }} ellipsis>
                                  {item.companyname}{item.deptname ? ` / ${item.deptname}` : ''}
                                </Text>
                                {item.position && (
                                  <Text style={{ fontSize: '13px', color: '#8c8c8c', marginLeft: '8px' }}>
                                    {item.position}
                                  </Text>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* 詳細展開ボタン */}
                          <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '8px' }}>
                            <Button
                              type="text"
                              icon={expandedItems.has(item.customerno || item.no) ? <UpOutlined /> : <DownOutlined />}
                              onClick={e => {
                                e.stopPropagation();
                                setExpandedItems(prev => {
                                  const next = new Set(prev);
                                  const id = item.customerno || item.no;
                                  next.has(id) ? next.delete(id) : next.add(id);
                                  return next;
                                });
                              }}
                              style={{ padding: '4px 8px' }}
                            />
                          </div>
                        </div>

                        {/* 展開詳細 */}
                        {expandedItems.has(item.customerno || item.no) && (
                          <div style={{
                            padding: '12px',
                            marginTop: '8px',
                            backgroundColor: '#f5f9fd',
                            borderRadius: '4px',
                            borderTop: '1px dashed #e8e8e8',
                          }}>
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                <MailOutlined style={{ marginRight: '8px', marginTop: '4px', color: '#0693e3' }} />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: '13px', color: '#0693e3', marginBottom: '4px', fontWeight: 'bold' }}>E-mail</div>
                                  <div style={{ fontSize: '14px' }}>{item.mailaddress || '未登録'}</div>
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                <HomeOutlined style={{ marginRight: '8px', marginTop: '4px', color: '#0693e3' }} />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: '13px', color: '#0693e3', marginBottom: '4px', fontWeight: 'bold' }}>住所</div>
                                  <div style={{ fontSize: '14px' }}>{item.address || '未登録'}</div>
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                <FileTextOutlined style={{ marginRight: '8px', marginTop: '4px', color: '#0693e3' }} />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: '13px', color: '#0693e3', marginBottom: '4px', fontWeight: 'bold' }}>備考</div>
                                  <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{item.remarks || '未登録'}</div>
                                </div>
                              </div>
                            </Space>
                          </div>
                        )}
                      </div>
                    </List.Item>
                  ); }}
                />
                {hasMore && (
                  <div style={{ textAlign: 'center', margin: '20px 0' }}>
                    <Spin tip="読み込み中..." />
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '32px', background: '#fff', borderRadius: '4px', marginTop: '16px' }}>
                <Text>登録済みの名刺がありません</Text>
              </div>
            )}
          </Content>
        </div>
      </Layout>
    </div>
  );
};

export default CustomerSelectPanel;
